// components/contracts/ContractDocumentsCard.js
// Card per upload e visualizzazione documenti con UI avanzata

import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { 
  FileText, Upload, Trash2, Eye, Download, CheckCircle, 
  AlertCircle, Loader2, Shield, Archive, Clock, XCircle,
  RefreshCw, History, AlertTriangle, FileX
} from 'lucide-react';
import { formatDate } from '../../lib/utils';
import { toast } from 'sonner';

/**
 * Configurazione tipi documento per contratti.
 * 
 * SEMPRE OBBLIGATORI: contratto_firmato, documento_identita (fronte+retro o unico)
 * CONDIZIONALI: visura_catastale, progetto_stato_luoghi, progetto_stato_progetto
 * OPZIONALI: preventivo_firmato, capitolato_firmato, progetto_demolizioni, altro
 */
const DOCUMENT_TYPES = [
  { value: 'contratto_firmato', label: 'Contratto Firmato', alwaysRequired: true },
  // Documento Identità - fronte/retro separati o file unico
  { value: 'documento_identita_fronte', label: 'Documento di Identità - Fronte', sensitive: true, identityPart: 'fronte' },
  { value: 'documento_identita_retro', label: 'Documento di Identità - Retro', sensitive: true, identityPart: 'retro' },
  { value: 'documento_identita', label: 'Documento di Identità - File unico', sensitive: true, identityPart: 'unico', note: 'PDF fronte+retro insieme' },
  { value: 'visura_catastale', label: 'Visura Catastale', conditional: true, note: 'Se necessaria' },
  { value: 'progetto_stato_luoghi', label: 'Stato dei Luoghi', conditional: true, note: 'Se necessario' },
  { value: 'progetto_stato_progetto', label: 'Stato di Progetto', conditional: true, note: 'Se necessario' },
  { value: 'preventivo_firmato', label: 'Preventivo Firmato', note: 'Opzionale' },
  { value: 'capitolato_firmato', label: 'Capitolato Firmato', note: 'Se separato' },
  { value: 'progetto_demolizioni', label: 'Demolizioni' },
  { value: 'altro', label: 'Altro Documento' },
];

// Documenti obbligatori escluso documento_identita (gestito a parte)
const ALWAYS_REQUIRED_BASE = DOCUMENT_TYPES.filter(d => d.alwaysRequired);

// Tipi documento identità
const IDENTITY_TYPES = ['documento_identita', 'documento_identita_fronte', 'documento_identita_retro'];

const getDocTypeConfig = (value) => DOCUMENT_TYPES.find(t => t.value === value) || { label: value };

const getStatusBadge = (status) => {
  switch (status) {
    case 'validated':
      return <Badge className="bg-green-100 text-green-700 border-green-200">Validato</Badge>;
    case 'rejected':
      return <Badge className="bg-red-100 text-red-700 border-red-200">Respinto</Badge>;
    case 'archived':
      return <Badge className="bg-gray-100 text-gray-600 border-gray-200">Archiviato</Badge>;
    case 'pending':
    default:
      return <Badge className="bg-amber-100 text-amber-700 border-amber-200">In attesa</Badge>;
  }
};

/**
 * Badge per file_status dei documenti.
 * 
 * available - File OK
 * missing - File mancante su storage
 * legacy_metadata_only - Metadati legacy senza file
 * orphaned - File orfano
 * unknown - Non verificato
 */
const getFileStatusBadge = (fileStatus, fileStatusReason) => {
  switch (fileStatus) {
    case 'missing':
      return (
        <Badge 
          className="bg-red-100 text-red-700 border-red-200" 
          title={fileStatusReason || 'File non trovato nello storage'}
        >
          <FileX className="w-3 h-3 mr-1" />
          File mancante
        </Badge>
      );
    case 'legacy_metadata_only':
      return (
        <Badge 
          className="bg-orange-100 text-orange-700 border-orange-200"
          title={fileStatusReason || 'Documento legacy senza file collegato'}
        >
          <AlertTriangle className="w-3 h-3 mr-1" />
          Documento legacy
        </Badge>
      );
    case 'orphaned':
      return (
        <Badge 
          className="bg-purple-100 text-purple-700 border-purple-200"
          title="File orfano"
        >
          <AlertCircle className="w-3 h-3 mr-1" />
          Orfano
        </Badge>
      );
    case 'unknown':
      return (
        <Badge 
          className="bg-gray-100 text-gray-500 border-gray-200"
          title="Stato file non verificato"
        >
          Stato sconosciuto
        </Badge>
      );
    case 'available':
    default:
      // Non mostrare badge se file è disponibile (stato normale)
      return null;
  }
};

/**
 * Verifica se il download è disabilitato per un documento
 */
const isDownloadDisabled = (doc) => {
  const fileStatus = doc.file_status;
  return fileStatus === 'missing' || fileStatus === 'legacy_metadata_only' || fileStatus === 'orphaned';
};

/**
 * Verifica se il documento di identità è completo.
 * Completo se:
 * - Esiste documento_identita (file unico) attivo, OPPURE
 * - Esistono ENTRAMBI documento_identita_fronte E documento_identita_retro attivi
 */
const isIdentityComplete = (uploadedTypes) => {
  const hasUnico = uploadedTypes.has('documento_identita');
  const hasFronte = uploadedTypes.has('documento_identita_fronte');
  const hasRetro = uploadedTypes.has('documento_identita_retro');
  
  return hasUnico || (hasFronte && hasRetro);
};

/**
 * Ottiene lo stato dettagliato del documento di identità.
 */
const getIdentityStatus = (uploadedTypes) => {
  const hasUnico = uploadedTypes.has('documento_identita');
  const hasFronte = uploadedTypes.has('documento_identita_fronte');
  const hasRetro = uploadedTypes.has('documento_identita_retro');
  
  if (hasUnico) {
    return { complete: true, mode: 'unico', fronte: true, retro: true };
  }
  
  if (hasFronte && hasRetro) {
    return { complete: true, mode: 'separato', fronte: true, retro: true };
  }
  
  return { 
    complete: false, 
    mode: hasFronte || hasRetro ? 'separato' : null,
    fronte: hasFronte, 
    retro: hasRetro 
  };
};

const ContractDocumentsCard = ({
  contract,
  contractId,
  canUpload,
  canValidate,
  uploading,
  selectedDocType,
  setSelectedDocType,
  docNotes,
  setDocNotes,
  fileInputRef,
  onFileUpload,
  onDeleteDocument,
  onDownloadDocument,
  onValidateDocument,
  onRefreshDocuments
}) => {
  const [showArchived, setShowArchived] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);

  // Documenti dalla collection crm_documents (passati dal parent) o dal contratto
  const allDocuments = contract.documentsFromAPI || contract.documents || [];
  
  // Filtra documenti attivi vs archiviati
  const activeDocuments = allDocuments.filter(d => d.status !== 'archived' && d.status !== 'deleted');
  const archivedDocuments = allDocuments.filter(d => d.status === 'archived');
  
  // Tipi già caricati (solo versioni attive)
  const uploadedDocTypes = new Set(activeDocuments.map(d => d.document_type));
  
  // Stato documento identità
  const identityStatus = getIdentityStatus(uploadedDocTypes);
  
  // Documenti obbligatori base mancanti (escluso documento_identita che ha logica speciale)
  const missingBaseDocs = ALWAYS_REQUIRED_BASE.filter(d => !uploadedDocTypes.has(d.value));
  
  // Tutti i documenti obbligatori sono caricati?
  const allRequiredUploaded = missingBaseDocs.length === 0 && identityStatus.complete;

  // Handler per download
  const handleDownload = async (doc) => {
    try {
      setActionLoading(doc.id);
      if (onDownloadDocument) {
        await onDownloadDocument(doc);
      } else {
        // Fallback: usa fetch con gestione errori
        const token = localStorage.getItem('token');
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/documents/${doc.id}/download`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        
        if (response.status === 410) {
          toast.error(
            'File non disponibile: il documento risulta registrato nel CRM, ma il file fisico non è più presente nello storage.',
            { duration: 8000 }
          );
          return;
        }
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = doc.original_filename || 'documento';
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        toast.success('Download completato');
      }
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Non hai i permessi per scaricare questo documento');
      } else if (error.response?.status === 410) {
        toast.error('File non disponibile nello storage', { duration: 5000 });
      } else {
        toast.error('Errore durante il download');
      }
    } finally {
      setActionLoading(null);
    }
  };

  // Handler per validazione
  const handleValidate = async (doc, status) => {
    try {
      setActionLoading(doc.id);
      if (onValidateDocument) {
        await onValidateDocument(doc.id, status);
        toast.success(status === 'validated' ? 'Documento validato' : 'Documento respinto');
        if (onRefreshDocuments) onRefreshDocuments();
      }
    } catch (error) {
      toast.error('Errore durante la validazione');
    } finally {
      setActionLoading(null);
    }
  };

  // Handler per archiviazione/delete
  const handleDelete = async (doc) => {
    if (!window.confirm('Vuoi archiviare questo documento?')) return;
    
    try {
      setActionLoading(doc.id);
      if (onDeleteDocument) {
        await onDeleteDocument(doc.id);
        toast.success('Documento archiviato');
        if (onRefreshDocuments) onRefreshDocuments();
      }
    } catch (error) {
      toast.error('Errore durante l\'archiviazione');
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <Card className="card-abitalo">
      <CardHeader>
        <CardTitle className="text-lg flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-[#FF7819]" />
            Documenti
          </div>
          {onRefreshDocuments && (
            <Button variant="ghost" size="sm" onClick={onRefreshDocuments}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        
        {/* === SEZIONE A: Documenti Obbligatori Mancanti === */}
        {!allRequiredUploaded ? (
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-amber-800">Documenti obbligatori mancanti</p>
                <ul className="text-xs text-amber-700 mt-1 space-y-0.5">
                  {/* Documenti base mancanti */}
                  {missingBaseDocs.map(d => (
                    <li key={d.value} className="flex items-center gap-1">
                      • {d.label}
                      {d.sensitive && <Shield className="w-3 h-3 text-amber-500" />}
                    </li>
                  ))}
                  
                  {/* Stato Documento di Identità */}
                  {!identityStatus.complete && (
                    <li className="flex items-center gap-1">
                      • Documento di Identità
                      <Shield className="w-3 h-3 text-amber-500" />
                      <span className="text-amber-600 ml-1">
                        {identityStatus.mode === 'separato' ? (
                          <>
                            ({identityStatus.fronte ? '✓ Fronte' : '✗ Fronte'}, 
                            {identityStatus.retro ? ' ✓ Retro' : ' ✗ Retro'})
                          </>
                        ) : (
                          '(Fronte e Retro, oppure file unico)'
                        )}
                      </span>
                    </li>
                  )}
                </ul>
              </div>
            </div>
          </div>
        ) : (
          <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <p className="text-sm font-medium text-green-800">
                Tutti i documenti obbligatori sono stati caricati
              </p>
            </div>
          </div>
        )}

        {/* === SEZIONE UPLOAD === */}
        {canUpload && !['approvato', 'respinto'].includes(contract.status) && (
          <div className="p-4 border-2 border-dashed border-[#DCDCDC] rounded-lg bg-gray-50/50">
            <div className="flex gap-4">
              <div className="flex-1">
                <Label className="text-sm font-medium">Tipo documento</Label>
                <Select value={selectedDocType} onValueChange={setSelectedDocType}>
                  <SelectTrigger className="mt-1" data-testid="doc-type-select">
                    <SelectValue placeholder="Seleziona tipo..." />
                  </SelectTrigger>
                  <SelectContent>
                    {DOCUMENT_TYPES.map(type => {
                      const isUploaded = uploadedDocTypes.has(type.value);
                      return (
                        <SelectItem key={type.value} value={type.value}>
                          <span className="flex items-center gap-2">
                            {type.label}
                            {type.alwaysRequired && !isUploaded && (
                              <span className="text-xs text-red-500">*</span>
                            )}
                            {type.sensitive && <Shield className="w-3 h-3 text-purple-500" />}
                            {isUploaded && <CheckCircle className="w-3 h-3 text-green-500" />}
                            {type.note && <span className="text-xs text-gray-400">({type.note})</span>}
                          </span>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1">
                <Label className="text-sm font-medium">Note (opzionale)</Label>
                <Input
                  value={docNotes}
                  onChange={(e) => setDocNotes(e.target.value)}
                  placeholder="Es: Fronte e retro"
                  className="mt-1"
                  data-testid="doc-notes-input"
                />
              </div>
            </div>
            
            <div className="mt-4">
              <input
                type="file"
                ref={fileInputRef}
                onChange={onFileUpload}
                accept=".pdf,.jpg,.jpeg,.png,.webp"
                className="hidden"
                data-testid="file-input"
              />
              <Button
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading || !selectedDocType}
                className="w-full border-[#FF7819] text-[#FF7819] hover:bg-[#FFF0E5]"
                data-testid="upload-doc-btn"
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Caricamento...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Carica Documento
                  </>
                )}
              </Button>
              <p className="text-xs text-center text-[#737373] mt-2">
                PDF, JPG, PNG - Max 10MB
              </p>
            </div>
          </div>
        )}

        {/* === SEZIONE B: Documenti Caricati === */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold text-gray-700">
              Documenti Caricati ({activeDocuments.length})
            </h4>
            {archivedDocuments.length > 0 && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setShowArchived(!showArchived)}
                className="text-xs"
              >
                <History className="w-3 h-3 mr-1" />
                {showArchived ? 'Nascondi' : 'Mostra'} archiviati ({archivedDocuments.length})
              </Button>
            )}
          </div>

          {activeDocuments.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-4">
              Nessun documento caricato
            </p>
          ) : (
            <div className="border rounded-lg overflow-x-auto">
              <table className="w-full min-w-[720px] text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left p-3 font-medium text-gray-600">Tipo</th>
                    <th className="text-left p-3 font-medium text-gray-600">File</th>
                    <th className="text-center p-3 font-medium text-gray-600">Ver.</th>
                    <th className="text-center p-3 font-medium text-gray-600">Stato</th>
                    <th className="text-left p-3 font-medium text-gray-600">Caricato</th>
                    <th className="text-right p-3 font-medium text-gray-600">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {activeDocuments.map((doc) => {
                    const typeConfig = getDocTypeConfig(doc.document_type);
                    const isSensitive = doc.is_sensitive || typeConfig.sensitive;
                    const downloadDisabled = isDownloadDisabled(doc);
                    const fileStatusBadge = getFileStatusBadge(doc.file_status, doc.file_status_reason);
                    
                    return (
                      <tr key={doc.id} className={`border-b last:border-0 hover:bg-gray-50 ${downloadDisabled ? 'bg-red-50/30' : ''}`}>
                        <td className="p-3">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium">{typeConfig.label}</span>
                            {isSensitive && (
                              <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200">
                                <Shield className="w-3 h-3 mr-1" />
                                Sensibile
                              </Badge>
                            )}
                            {/* Badge file_status per documenti mancanti/legacy */}
                            {fileStatusBadge}
                          </div>
                        </td>
                        <td className="p-3">
                          <div className="max-w-[200px]">
                            <p className={`truncate ${downloadDisabled ? 'text-gray-400' : 'text-gray-900'}`}>
                              {doc.original_filename}
                            </p>
                            {doc.notes && (
                              <p className="text-xs text-gray-500 truncate">{doc.notes}</p>
                            )}
                            {/* Tooltip/messaggio per file mancante */}
                            {downloadDisabled && (
                              <p className="text-xs text-red-600 mt-1">
                                {doc.file_status === 'legacy_metadata_only' 
                                  ? 'Metadati presenti, file non collegato' 
                                  : 'File non presente nello storage'}
                              </p>
                            )}
                          </div>
                        </td>
                        <td className="p-3 text-center">
                          <Badge variant="outline" className="text-xs">v{doc.version || 1}</Badge>
                        </td>
                        <td className="p-3 text-center">
                          {getStatusBadge(doc.status)}
                        </td>
                        <td className="p-3">
                          <div className="text-xs text-gray-500">
                            <p>{doc.uploaded_by_name || 'N/A'}</p>
                            <p>{doc.uploaded_at ? formatDate(doc.uploaded_at) : doc.created_at ? formatDate(doc.created_at) : '-'}</p>
                          </div>
                        </td>
                        <td className="p-3">
                          <div className="flex items-center justify-end gap-1">
                            {/* Download - disabilitato se file mancante */}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => !downloadDisabled && handleDownload(doc)}
                              disabled={actionLoading === doc.id || downloadDisabled}
                              title={downloadDisabled ? 'File non disponibile - Ricaricare il documento' : 'Scarica'}
                              className={downloadDisabled ? 'opacity-50 cursor-not-allowed' : ''}
                            >
                              {actionLoading === doc.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : downloadDisabled ? (
                                <FileX className="w-4 h-4 text-red-400" />
                              ) : (
                                <Download className="w-4 h-4 text-blue-600" />
                              )}
                            </Button>
                            
                            {/* Validate (solo per chi può validare e doc pending) */}
                            {canValidate && doc.status === 'pending' && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleValidate(doc, 'validated')}
                                  disabled={actionLoading === doc.id}
                                  title="Valida"
                                >
                                  <CheckCircle className="w-4 h-4 text-green-600" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleValidate(doc, 'rejected')}
                                  disabled={actionLoading === doc.id}
                                  title="Respingi"
                                >
                                  <XCircle className="w-4 h-4 text-red-600" />
                                </Button>
                              </>
                            )}
                            
                            {/* Archive/Delete */}
                            {canUpload && doc.status !== 'archived' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDelete(doc)}
                                disabled={actionLoading === doc.id}
                                title="Archivia"
                              >
                                <Archive className="w-4 h-4 text-gray-500" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Documenti Archiviati */}
          {showArchived && archivedDocuments.length > 0 && (
            <div className="mt-4">
              <h5 className="text-xs font-medium text-gray-500 mb-2 flex items-center gap-1">
                <Archive className="w-3 h-3" />
                Versioni Archiviate
              </h5>
              <div className="border rounded-lg overflow-x-auto opacity-75">
                <table className="w-full min-w-[720px] text-sm">
                  <tbody>
                    {archivedDocuments.map((doc) => {
                      const typeConfig = getDocTypeConfig(doc.document_type);
                      return (
                        <tr key={doc.id} className="border-b last:border-0 bg-gray-50">
                          <td className="p-2 text-gray-500">{typeConfig.label}</td>
                          <td className="p-2 text-gray-500 truncate max-w-[150px]">{doc.original_filename}</td>
                          <td className="p-2 text-center"><Badge variant="outline" className="text-xs">v{doc.version || 1}</Badge></td>
                          <td className="p-2 text-xs text-gray-400">{doc.uploaded_at ? formatDate(doc.uploaded_at) : '-'}</td>
                          <td className="p-2 text-right">
                            <Button variant="ghost" size="sm" onClick={() => handleDownload(doc)}>
                              <Download className="w-3 h-3 text-gray-400" />
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default ContractDocumentsCard;
